import * as React from "react";
import Section from "../layout/Section";
import { useAnimation } from "../../hooks/useAnimate";
import { TitleHeading } from "../typography/TitleHeading";
import BodyText from "../typography/BodyText";
import { Pattern } from "../Pattern";
import { twMerge } from "tailwind-merge";
import { useUseCases } from "../../hooks/useUseCases";
import { LinkCta } from "../typography/LinkCta";
import { useUseCaseI18nLink } from "../../hooks/useI18nLink";
import { useI18next, useTranslation } from "gatsby-plugin-react-i18next";
import Keyword from "../typography/Keyword";
import GridBlock from "../grid-block/GridBlock";

export const UseCases = (props: {
  tag: string;
  title: JSX.Element | string;
  description?: JSX.Element | string;
  theme?: "dark" | "white" | "gray";
  centerTitle?: boolean;
  bgTag?: boolean;
  target?: string;
}) => {
  const { id, animate } = useAnimation();
  const useCases = useUseCases();
  const { language } = useI18next();
  const { useCaseLink } = useUseCaseI18nLink();
  const { t } = useTranslation();

  return (
    <Section theme={props.theme} id={id}>
      <div {...animate("fade-down")}>
        <TitleHeading
          tag={props.tag}
          title={props.title}
          theme={props.theme}
          center={props.centerTitle}
          bgTag={props.bgTag ?? false}
        />
        {props.description && (
          <BodyText
            className={twMerge(
              "max-w-4xl mt-3",
              props.centerTitle && "mx-auto text-center",
            )}
          >
            {props.description}
          </BodyText>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-1 gap-y-16 mt-14">
        {useCases.slice(0, 4).map((useCase, i) => (
          <div
            key={`block${i}`}
            {...animate(i % 2 === 0 ? "fade-right" : "fade-left", 100 * i)}
            className={twMerge("flex flex-col justify-start")}
          >
            <div className="h-18 flex flex-col justify-center overflow-x-clip">
              <img
                src={
                  require(`../../assets/images/clients/${useCase.client.logo}`)
                    .default
                }
                alt={useCase.client.client}
                className="h-8 mx-auto mb-2"
              />
              <p className="z-50 text-center max-w-xl text-md whitespace-nowrap font-bold text-gray-500 overflow-ellipsis mx-auto">
                {useCase.hero.projectName[language]}
              </p>
            </div>
            <div className="grow bg-gray-100 w-full mt-2 mb-2 px-4 py-8 lg:py-14 grid grid-cols-2 gap-x-3">
              {useCase.result.keyResults.slice(0, 2).map((item, index) => (
                <div
                  key={index}
                  className="flex flex-col items-center justify-center"
                >
                  <p className="text-3xl xl:text-4xl font-bold text-primary text-center _whitespace-nowrap">
                    {item[language][0]}
                  </p>
                  <p className="text-center text-gray-900 font-semibold mt-1 leading-4 lg:text-lg lg:leading-5">
                    {item[language][1]}
                  </p>
                </div>
              ))}
            </div>
            <LinkCta
              to={useCaseLink(useCase)}
              text={t("index:useCase.ctaLink")}
              align="right"
              color="text-gray-600"
              target={props.target}
            />
          </div>
        ))}
      </div>
    </Section>
  );
};
